import React from "react";
import { Box, Typography, useMediaQuery, useTheme, Card } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { QualitySmartSolutionCardImg } from "../../constant";

const MainContainer = styled(Box)(({ theme }) => ({
  background: "#000",
  display: "flex",
  flexDirection: "column",
  padding: "60px 24px",
  position: "relative",
  overflow: "hidden",

  "&::after": {
    content: '""',
    position: "absolute",
    width: "65%",
    height: "50%",
    left: "75%",
    top: "20%",
    bottom: "10%",
    transform: "translateX(-100%), translateY(-50%)",
    background:
      "radial-gradient(circle, rgba(99, 102, 241, 0.65) 0%, rgba(99, 102, 241, 0) 70%)",
    filter: "blur(100px)",
    //backdropFilter: "blur(500px)",
    zIndex: 0,
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  position: "relative",
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const StyledCard = styled(motion.create(Card))(({ theme }) => ({
  width: "250px",
  height: "200px",
  border: "0.64px solid transparent",
  borderRadius: "12px",
  background:
    "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 239, 255, 0.2) 0%, rgba(110, 191, 244, 0.0447917) 77.08%, rgba(70, 144, 213, 0) 100%)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "inherit",
    padding: "0.64px",
    background:
      "radial-gradient(80.38% 222.5% at -13.75% -12.36%, #98F9FF 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(80.69% 208.78% at 108.28% 112.58%, #EABFFF 0%, rgba(135, 38, 183, 0) 100%)",
    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    maskComposite: "exclude",
    WebkitMaskComposite: "xor",
  },
  position: "relative",
  overflow: "hidden",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "24px",
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    maxWidth: "280px",
  },
}));

const StepNumber = styled(Typography)(({ theme }) => ({
  position: "absolute",
  top: "16px",
  right: "16px",
  fontSize: "48px",
  fontWeight: "700",
  opacity: 0.4,
  color: "#4B5563",
}));

const StepIcon = styled(motion.div)(({ theme }) => ({
  width: "40px",
  height: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const ConnectorLine = styled(motion.div)(({ theme, isvertical }) => ({
  height: isvertical ? "60px" : "2px",
  width: isvertical ? "2px" : "100px",
  background: "#61D1FA",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    height: "60px",
    width: "2px",
  },
}));

const QualitySmartSoltion = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const stepsData = [
    {
      id: "01",
      title: "Data Collection",
      description:
        "Capture quality, production and laboratory data automatically from plants, process equipment, ERP systems and testing environments in real time",
      icon: QualitySmartSolutionCardImg,
    },
    {
      id: "02",
      title: "Data Integration", 
      description:
        "Unify data from internal labs, third-party laboratories, production systems and operational workflows into a centralized quality management platform.",
      icon: QualitySmartSolutionCardImg,
    },
    {
      id: "03",
      title: "Analytics & Financial Insights",
      description:
        "Analyze grade variations, process deviations and quality trends while evaluating their financial impact on pricing, recovery and profitability.",
      icon: QualitySmartSolutionCardImg,
    },
    {
      id: "04",
      title: "Automated Reporting & Decision Support",
      description:
        "Generate automated dashboards, compliance reports and operational insights to enable faster, data-driven quality and business decisions.",
      icon: QualitySmartSolutionCardImg,
    },
  ];

  const cardVariants = {
    initial: {
      y: 0,
      scale: 1,
    },
  };

  const iconVariants = {
    initial: { opacity: 1, scale: 1 },
    hover: { opacity: 0, scale: 0.5 },
  };

  const titleVariants = {
    initial: { y: 50, opacity: 1 },
    hover: { y: -30, opacity: 1, transition: { delay: 0 } },
  };

  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    hover: {
      opacity: 1,
      y: -25,
      transition: { delay: 0 },
    },
  };

  return (
    <MainContainer>
      <ContentContainer>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          width={isMobile ? "100%" : "100%"}
          margin="0 auto"
        >
          {/* Header Section */}
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            width="100%"
            textAlign="center"
          >
            <Typography
              variant="h2"
              sx={{
                background: "linear-gradient(135deg, #6366F1 0%, #3B82F6 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "24px", sm: "32px", md: "40px" },
                fontWeight: 700,
                textAlign: "center",
                mt: { xs: 5, md: 7 },
              }}
            >
              Smart Quality Management in Action
            </Typography>
            
            <Typography
              sx={{
                color: "#8C8CC2",
                fontSize: { xs: "14px", md: "16px" },
                lineHeight: 1.8,
                textAlign: "center",
                marginTop: 2,
                marginBottom: 8,
              }}
            >
             Discover how intelligent quality management solutions streamline data collection, integrate operational and laboratory systems, and deliver real-time insights for improved quality control and business performance
            </Typography>
          </Box>
        </Box>

        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="center"
          alignItems="center"
          width="100%"
          mt={4}
        >
          {stepsData?.map((step, index) => (
            <React.Fragment key={step.id}>
              <motion.div
                initial="initial"
                whileHover="hover"
                style={{ display: "flex", justifyContent: "center" }}
              >
                <StyledCard variants={cardVariants}>
                  <StepNumber>{step.id}</StepNumber>
                  <StepIcon variants={iconVariants}>
                    <img src={step.icon} alt="icon" />
                  </StepIcon>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <motion.div variants={titleVariants}>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontSize: "18px",
                          fontWeight: 400,
                          textAlign: "left",
                          mb: 1,
                        }}
                      >
                        {step.title}
                      </Typography>
                    </motion.div>

                    <motion.div variants={contentVariants}>
                      <Typography
                        sx={{
                          color: "#fff",
                          fontSize: "12px",
                          lineHeight: 1.5,
                          textAlign: "left",
                        }}
                      >
                        {step.description}
                      </Typography>
                    </motion.div>
                  </Box>
                </StyledCard>
              </motion.div>

              {index < stepsData.length - 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    m: isMobile ? 2 : 0,
                  }}
                >
                  <ConnectorLine
                    isvertical={isMobile}
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </ContentContainer>
    </MainContainer>
  );
};

export default QualitySmartSoltion;
